/**
 * PRIMA GOLD - Luxury Product Lightbox Modal & Multi-Image Gallery
 * Features:
 * - Prev / Next navigation arrows
 * - Live photo counter (e.g. 1 / 4)
 * - Keyboard ArrowLeft / ArrowRight / Escape navigation
 * - Touch swipe left / right
 * - Interactive Pan-to-Inspect (Soi chi tiết) inside modal
 */
if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentIndex = 0;
        this.mediaList = [];
        this.counterEl = this.querySelector('.prima-modal-counter');
        this.prevBtn = this.querySelector('.prima-modal-nav-prev');
        this.nextBtn = this.querySelector('.prima-modal-nav-next');
        this.contentEl = this.querySelector('.product-media-modal__content');

        this.bindModalEvents();
      }

      bindModalEvents() {
        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.prev();
          });
        }

        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.next();
          });
        }

        // Keyboard navigation
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.prev();
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.next();
          }
        });

        // Touch Swipe
        let touchStartX = 0;
        let touchEndX = 0;
        this.addEventListener(
          'touchstart',
          (e) => {
            touchStartX = e.changedTouches[0].screenX;
          },
          { passive: true }
        );

        this.addEventListener(
          'touchend',
          (e) => {
            touchEndX = e.changedTouches[0].screenX;
            const diff = touchEndX - touchStartX;
            if (Math.abs(diff) > 45) {
              if (diff > 0) this.prev();
              else this.next();
            }
          },
          { passive: true }
        );

        // Interactive Soi Chi Tiết (Pan / Hover Zoom) on active modal image
        if (this.contentEl) {
          this.contentEl.addEventListener('mousemove', (e) => {
            const activeImg = this.contentEl.querySelector('img.active, [data-media-id].active img, [data-media-id].active');
            if (!activeImg || activeImg.tagName !== 'IMG') return;

            const rect = activeImg.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return;

            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const percentX = Math.max(0, Math.min(100, (x / rect.width) * 100));
            const percentY = Math.max(0, Math.min(100, (y / rect.height) * 100));

            activeImg.style.transformOrigin = `${percentX}% ${percentY}%`;
          });
        }
      }

      show(opener) {
        super.show(opener);
        this.refreshMediaList();
        this.showActiveMedia();
      }

      refreshMediaList() {
        this.mediaList = Array.from(this.querySelectorAll('.product-media-modal__content [data-media-id]'));
        const hasMultiple = this.mediaList.length > 1;
        if (this.prevBtn && this.nextBtn) {
          this.prevBtn.style.display = hasMultiple ? 'flex' : 'none';
          this.nextBtn.style.display = hasMultiple ? 'flex' : 'none';
        }
        if (this.counterEl) {
          this.counterEl.style.display = hasMultiple ? 'block' : 'none';
        }
      }

      showActiveMedia() {
        this.refreshMediaList();
        if (this.mediaList.length === 0) return;

        const mediaId = this.openedBy
          ? this.openedBy.getAttribute('data-media-id') ||
            this.openedBy.querySelector('[data-media-id]')?.getAttribute('data-media-id')
          : null;

        let index = -1;
        if (mediaId) {
          index = this.mediaList.findIndex((el) => el.getAttribute('data-media-id') === mediaId);
        }

        this.currentIndex = index >= 0 ? index : 0;
        this.renderCurrentMedia();
      }

      renderCurrentMedia() {
        if (!this.mediaList || this.mediaList.length === 0) return;

        this.mediaList.forEach((element, i) => {
          if (i === this.currentIndex) {
            element.classList.add('active');
            element.style.display = 'block';
          } else {
            element.classList.remove('active');
            element.style.display = 'none';
          }
        });

        // Update counter
        if (this.counterEl) {
          this.counterEl.textContent = `${this.currentIndex + 1} / ${this.mediaList.length}`;
        }

        const activeMedia = this.mediaList[this.currentIndex];
        if (activeMedia) {
          const activeMediaTemplate = activeMedia.querySelector('template');
          const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
          if (
            activeMedia.nodeName === 'DEFERRED-MEDIA' &&
            activeMediaContent &&
            activeMediaContent.querySelector('.js-youtube')
          ) {
            activeMedia.loadContent();
          }
        }
      }

      next() {
        if (this.mediaList.length <= 1) return;
        this.currentIndex = (this.currentIndex + 1) % this.mediaList.length;
        this.renderCurrentMedia();
      }

      prev() {
        if (this.mediaList.length <= 1) return;
        this.currentIndex = (this.currentIndex - 1 + this.mediaList.length) % this.mediaList.length;
        this.renderCurrentMedia();
      }
    }
  );
}
